import React, { useState, useEffect } from "react";
import {
  Card,
  Form,
  Container,
  Row,
  Col,
  Button,
  Tab,
  Tabs,
} from "react-bootstrap";
import {
  getAllSubscribePageSections,
  updateSubscribePageSection,
  addSubscribePageSection,
} from "../../actions/subscribePageAction";
import { useSelector } from "react-redux";
import FullPageLoader from "../../components/FullPageLoader/FullPageLoader";
import { toast } from "react-toastify";
import { useFormik } from "formik";
import * as Yup from "yup";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import SweetAlert from "react-bootstrap-sweetalert";

const SubscribePageManagement = () => {
  const [loader, setLoader] = useState(false);
  const [activeTab, setActiveTab] = useState("banner");
  const [sectionData, setSectionData] = useState({
    banner: null,
    subscriptionPlan: null,
    subscriptionGuide: null,
    betterChoice: null,
  });
  const [uploadImage, setUploadImage] = useState(false);
  const { admin } = useSelector((state) => state.admin);
  const currentUserRole = admin?.role;

  const getAllSections = async () => {
    setLoader(true);
    try {
      const res = await getAllSubscribePageSections();
      if (res?.data?.data) {
        setSectionData(res.data.data);
      }
    } catch (error) {
      console.log("Error fetching sections:", error);
    }
    setLoader(false);
  };

  useEffect(() => {
    getAllSections();
  }, []);

  const handleImagePreview = (e, setFieldValue, fieldPath) => {
    if (e.target.files[0]) {
      const file = e.target.files[0];
      const imageType = file.type.split("/")[1];
      if (
        imageType === "png" ||
        imageType === "jpg" ||
        imageType === "jpeg" ||
        imageType === "gif" ||
        imageType === "webp"
      ) {
        setFieldValue(fieldPath, file);
      } else {
        setUploadImage(true);
        e.target.value = null;
      }
    }
  };

  const hideModel = () => {
    setUploadImage(false);
  };

  // Banner Form – trim so spaces-only fail required
  const bannerValidation = Yup.object().shape({
    heading: Yup.string().trim().required("Heading is required"),
    description: Yup.string()
      .required("Description is required")
      .test("not-whitespace", "Description is required", (val) => {
        if (!val) return false;
        const stripped = val.replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim();
        return stripped.length > 0;
      }),
    price: Yup.string().trim().required("Price is required"),
  });

  const bannerFormik = useFormik({
    enableReinitialize: true,
    initialValues: {
      heading: sectionData.banner?.heading || "",
      description: sectionData.banner?.description || 
                   (sectionData.banner?.description1 ? 
                     `${sectionData.banner.description1}\n${sectionData.banner.description2 || ''}\n${sectionData.banner.description3 || ''}`.trim() 
                     : ""),
      price: sectionData.banner?.price || "",
      bannerImage: sectionData.banner?.bannerImage || null,
      status: sectionData.banner?.status !== undefined ? sectionData.banner.status : true,
    },
    validationSchema: bannerValidation,
    onSubmit: async (values) => {
      if (sectionData.banner?._id && !currentUserRole?.editSubscribePage) {
        toast.error("You don't have permission to edit subscribe page");
        return;
      }
      if (!sectionData.banner?._id && !currentUserRole?.addSubscribePage) {
        toast.error("You don't have permission to add subscribe page");
        return;
      }

      setLoader(true);
      const formData = new FormData();
      Object.keys(values).forEach((key) => {
        if (key !== "bannerImage") {
          formData.append(key, values[key]);
        }
      });
      if (values.bannerImage instanceof File) {
        formData.append("bannerImage", values.bannerImage);
      }

      try {
        let res;
        if (sectionData.banner?._id) {
          res = await updateSubscribePageSection(sectionData.banner._id, formData, "banner");
        } else {
          res = await addSubscribePageSection(formData, "banner");
        }
        if (res?.data?.success) {
          toast.success(res.data.message);
          getAllSections();
        } else {
          toast.error(res?.data?.message || "Failed to save banner");
        }
      } catch (error) {
        toast.error("An error occurred");
      }
      setLoader(false);
    },
  });

  // Subscription Plan Form
  const subscriptionPlanValidation = Yup.object().shape({
    heading: Yup.string().trim().required("Section heading is required"),
    price: Yup.string().trim().required("Price is required"),
    faqs: Yup.array().of(
      Yup.object().shape({
        question: Yup.string().trim().required("Question is required"),
        answer: Yup.string()
          .required("Answer is required")
          .test("not-whitespace", "Answer is required", (val) => {
            if (!val) return false;
            const stripped = (val || "")
              .replace(/<[^>]*>/g, "")
              .replace(/&nbsp;/g, " ")
              .trim();
            return stripped.length > 0;
          }),
      })
    ),
  });

  const subscriptionPlanFormik = useFormik({
    enableReinitialize: true,
    initialValues: {
      heading: sectionData.subscriptionPlan?.heading || "",
      planImage: sectionData.subscriptionPlan?.planImage || null,
      faqs: sectionData.subscriptionPlan?.faqs || [
        { question: "", answer: "", section_type: "2" },
      ],
      price: sectionData.subscriptionPlan?.price || "",
      status: sectionData.subscriptionPlan?.status !== undefined ? sectionData.subscriptionPlan.status : true,
    },
    validationSchema: subscriptionPlanValidation,
    onSubmit: async (values) => {
      if (sectionData.subscriptionPlan?._id && !currentUserRole?.editSubscribePage) {
        toast.error("You don't have permission to edit subscribe page");
        return;
      }
      if (!sectionData.subscriptionPlan?._id && !currentUserRole?.addSubscribePage) {
        toast.error("You don't have permission to add subscribe page");
        return;
      }

      setLoader(true);
      const formData = new FormData();
      formData.append("heading", values.heading);
      formData.append("faqs", JSON.stringify(values.faqs));
      formData.append("price", values.price);
      formData.append("status", values.status);
      if (values.planImage instanceof File) {
        formData.append("planImage", values.planImage);
      }

      try {
        let res;
        if (sectionData.subscriptionPlan?._id) {
          res = await updateSubscribePageSection(sectionData.subscriptionPlan._id, formData, "subscriptionPlan");
        } else {
          res = await addSubscribePageSection(formData, "subscriptionPlan");
        }
        if (res?.data?.success) {
          toast.success(res.data.message);
          getAllSections();
        } else {
          toast.error(res?.data?.message || "Failed to save subscription plan");
        }
      } catch (error) {
        toast.error("An error occurred");
      }
      setLoader(false);
    },
  });

  // Subscription Guide Form
  const subscriptionGuideFormik = useFormik({
    enableReinitialize: true,
    initialValues: {
      heading: sectionData.subscriptionGuide?.heading || "",
      status: sectionData.subscriptionGuide?.status !== undefined ? sectionData.subscriptionGuide.status : true,
    },
    onSubmit: async (values) => {
      if (sectionData.subscriptionGuide?._id && !currentUserRole?.editSubscribePage) {
        toast.error("You don't have permission to edit subscribe page");
        return;
      }
      if (!sectionData.subscriptionGuide?._id && !currentUserRole?.addSubscribePage) {
        toast.error("You don't have permission to add subscribe page");
        return;
      }

      setLoader(true);
      const formData = new FormData();
      Object.keys(values).forEach((key) => {
        formData.append(key, values[key]);
      });

      try {
        let res;
        if (sectionData.subscriptionGuide?._id) {
          res = await updateSubscribePageSection(sectionData.subscriptionGuide._id, formData, "subscriptionGuide");
        } else {
          res = await addSubscribePageSection(formData, "subscriptionGuide");
        }
        if (res?.data?.success) {
          toast.success(res.data.message);
          getAllSections();
        } else {
          toast.error(res?.data?.message || "Failed to save subscription guide");
        }
      } catch (error) {
        toast.error("An error occurred");
      }
      setLoader(false);
    },
  });

  // Better Choice validation – fields should not be empty
  const betterChoiceValidation = Yup.object().shape({
    heading: Yup.string().trim().required("Section heading is required"),
    caremd: Yup.object().shape({
      consultationCost: Yup.string().trim().required("Consultation cost is required"),
      patientReviews: Yup.string().trim().required("Patient reviews is required"),
      consultationMethod: Yup.string().trim().required("Consultation method is required"),
      requiresAppointment: Yup.string().trim().required("Requires appointment is required"),
      is247: Yup.boolean(),
    }),
    otherTelemedicine: Yup.object().shape({
      consultationCost: Yup.string().trim().required("Consultation cost is required"),
      patientReviews: Yup.string().trim().required("Patient reviews is required"),
      consultationMethod: Yup.string().trim().required("Consultation method is required"),
      requiresAppointment: Yup.string().trim().required("Requires appointment is required"),
      is247: Yup.boolean(),
    }),
    status: Yup.boolean(),
  });

  // Better Choice Form
  const betterChoiceFormik = useFormik({
    enableReinitialize: true,
    initialValues: {
      heading: sectionData.betterChoice?.heading || "",
      caremd: sectionData.betterChoice?.caremd || {
        consultationCost: "",
        patientReviews: "",
        consultationMethod: "",
        requiresAppointment: "",
        is247: true,
      },
      otherTelemedicine: sectionData.betterChoice?.otherTelemedicine || {
        consultationCost: "",
        patientReviews: "",
        consultationMethod: "",
        requiresAppointment: "",
        is247: false,
      },
      status: sectionData.betterChoice?.status !== undefined ? sectionData.betterChoice.status : true,
    },
    validationSchema: betterChoiceValidation,
    onSubmit: async (values) => {
      if (sectionData.betterChoice?._id && !currentUserRole?.editSubscribePage) {
        toast.error("You don't have permission to edit subscribe page");
        return;
      }
      if (!sectionData.betterChoice?._id && !currentUserRole?.addSubscribePage) {
        toast.error("You don't have permission to add subscribe page");
        return;
      }

      setLoader(true);
      const formData = new FormData();
      formData.append("heading", values.heading);
      formData.append("caremd", JSON.stringify(values.caremd));
      formData.append("otherTelemedicine", JSON.stringify(values.otherTelemedicine));
      formData.append("status", values.status);

      try {
        let res;
        if (sectionData.betterChoice?._id) {
          res = await updateSubscribePageSection(sectionData.betterChoice._id, formData, "betterChoice");
        } else {
          res = await addSubscribePageSection(formData, "betterChoice");
        }
        if (res?.data?.success) {
          toast.success(res.data.message);
          getAllSections();
        } else {
          toast.error(res?.data?.message || "Failed to save better choice");
        }
      } catch (error) {
        toast.error("An error occurred");
      }
      setLoader(false);
    },
  });

  const handleAddFAQ = (formik) => {
    const currentFAQs = formik.values.faqs || [];
    formik.setFieldValue("faqs", [
      ...currentFAQs,
      { question: "", answer: "", section_type: "2" },
    ]);
  };

  const handleRemoveFAQ = (formik, index) => {
    const currentFAQs = formik.values.faqs || [];
    const updatedFAQs = currentFAQs.filter((_, i) => i !== index);
    formik.setFieldValue("faqs", updatedFAQs);
  };

  if (loader && !sectionData.banner) {
    return <FullPageLoader />;
  }

  return (
    <>
      {uploadImage ? (
        <SweetAlert
          warning
          showCloseButton
          confirmBtnText="OK"
          onConfirm={hideModel}
          confirmBtnBsStyle="danger"
          title="Failed! Please select correct Image Format"
          onCancel={hideModel}
          focusCancelBtn
          customClass="del-sweet-alert"
        >
          Supported files ["png", "jpg", "jpeg", "gif", "webp"]
        </SweetAlert>
      ) : null}
      <div className="pt-2 pt-md-3 cms-updated-style subscribe-page-management">
        <Container fluid>
          <Row>
            <Col md="12">
              <div className="datatable-title card d-flex flex-row justify-content-between align-items-center mb-0">
                <h4 className="card-title">Subscribe Page Management</h4>
              </div>
              <Card className="filter-card card">
                <Card.Body>
                  <Tabs
                    activeKey={activeTab}
                    onSelect={(k) => setActiveTab(k)}
                    className="mb-3 custom-styled-tabs"
                  >
                    {/* Banner Tab */}
                    <Tab eventKey="banner" title="Banner">
                      <Form onSubmit={bannerFormik.handleSubmit}>
                        <Row>
                          <Col md={12}>
                            <Form.Group className="mb-3">
                              <Form.Label>
                                Heading <span className="text-danger">*</span>
                              </Form.Label>
                              <Form.Control
                                type="text"
                                name="heading"
                                value={bannerFormik.values.heading}
                                onChange={bannerFormik.handleChange}
                                placeholder="Enter banner heading"
                              />
                              {bannerFormik.touched.heading && bannerFormik.errors.heading && (
                                <span className="text-danger">{bannerFormik.errors.heading}</span>
                              )}
                            </Form.Group>
                          </Col>
                          <Col md={12}>
                            <Form.Group className="mb-3">
                              <Form.Label>
                                Description <span className="text-danger">*</span>
                              </Form.Label>
                              <CKEditor
                                editor={ClassicEditor}
                                data={bannerFormik.values.description || ""}
                                onChange={(event, editor) => {
                                  const data = editor.getData();
                                  bannerFormik.setFieldValue("description", data);
                                }}
                              />
                              {bannerFormik.touched.description && bannerFormik.errors.description && (
                                <span className="text-danger">{bannerFormik.errors.description}</span>
                              )}
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label>
                                Price <span className="text-danger">*</span>
                              </Form.Label>
                              <Form.Control
                                type="text"
                                name="price"
                                value={bannerFormik.values.price}
                                onChange={bannerFormik.handleChange}
                                placeholder="Enter price (e.g., $39.99/month)"
                              />
                              {bannerFormik.touched.price && bannerFormik.errors.price && (
                                <span className="text-danger">{bannerFormik.errors.price}</span>
                              )}
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label>Banner Image</Form.Label>
                              <Form.Control
                                type="file"
                                accept="image/*"
                                className="custom-banner-image"
                                onChange={(e) =>
                                  handleImagePreview(e, bannerFormik.setFieldValue, "bannerImage")
                                }
                              />
                              {bannerFormik.values.bannerImage && typeof bannerFormik.values.bannerImage === "string" && (
                                <img
                                  src={bannerFormik.values.bannerImage.startsWith("http") 
                                    ? bannerFormik.values.bannerImage 
                                    : `${process.env.REACT_APP_SERVER_URL || ""}/public/uploads/images/${bannerFormik.values.bannerImage}`}
                                  alt="Preview"
                                  style={{ width: "200px", marginTop: "10px" }}
                                />
                              )}
                            </Form.Group>
                          </Col>
                          <Col md={12}>
                            <Form.Group className="ml-3">
                              <Row>
                                <Col md={1}>
                                  <label className="label-font">Status</label>
                                </Col>
                                <Col md={11} className="check-inline d-flex">
                                  <label className="right-label-radio mr-3 mb-2">
                                    Active
                                    <input
                                      name="status"
                                      type="radio"
                                      checked={bannerFormik.values.status === true}
                                      onChange={() => bannerFormik.setFieldValue("status", true)}
                                    />
                                    <span className="checkmark"></span>
                                  </label>
                                  <label className="right-label-radio mr-3 mb-2">
                                    Inactive
                                    <input
                                      name="status"
                                      type="radio"
                                      checked={bannerFormik.values.status === false}
                                      onChange={() => bannerFormik.setFieldValue("status", false)}
                                    />
                                    <span className="checkmark"></span>
                                  </label>
                                </Col>
                              </Row>
                            </Form.Group>
                          </Col>
                        </Row>
                        <div className="d-flex justify-content-end mt-4">
                          <Button type="submit" className="btn-filled" disabled={loader}>
                            {loader ? "Saving..." : "Save Banner"}
                          </Button>
                        </div>
                      </Form>
                    </Tab>

                    {/* Subscription Plan Tab */}
                    <Tab eventKey="subscriptionPlan" title="Subscription Plan">
                      <Form onSubmit={subscriptionPlanFormik.handleSubmit}>
                        <Row>
                          <Col md={12}>
                            <Form.Group className="mb-3">
                              <Form.Label>
                                Section Heading <span className="text-danger">*</span>
                              </Form.Label>
                              <Form.Control
                                type="text"
                                name="heading"
                                value={subscriptionPlanFormik.values.heading}
                                onChange={subscriptionPlanFormik.handleChange}
                                onBlur={subscriptionPlanFormik.handleBlur}
                                placeholder="Enter section heading"
                              />
                              {subscriptionPlanFormik.touched.heading && subscriptionPlanFormik.errors.heading && (
                                <span className="text-danger d-block mt-1">{subscriptionPlanFormik.errors.heading}</span>
                              )}
                            </Form.Group>
                          </Col>
                          <Col md={12}>
                            <Form.Group className="mb-3">
                              <Form.Label>Plan Image</Form.Label>
                              <Form.Control
                                type="file"
                                accept="image/*"
                                className="custom-banner-image"
                                onChange={(e) =>
                                  handleImagePreview(e, subscriptionPlanFormik.setFieldValue, "planImage")
                                }
                              />
                              {subscriptionPlanFormik.values.planImage && typeof subscriptionPlanFormik.values.planImage === "string" && (
                                <img
                                  src={subscriptionPlanFormik.values.planImage.startsWith("http") 
                                    ? subscriptionPlanFormik.values.planImage 
                                    : `${process.env.REACT_APP_SERVER_URL || ""}/public/uploads/images/${subscriptionPlanFormik.values.planImage}`}
                                  alt="Preview"
                                  style={{ width: "200px", marginTop: "10px" }}
                                />
                              )}
                            </Form.Group>
                          </Col>
                          <Col md={12}>
                            <div className="d-flex justify-content-between align-items-center mb-3">
                              <Form.Label>FAQs</Form.Label>
                              <Button
                                type="button"
                                className="btn-filled"
                                size="sm"
                                onClick={() => handleAddFAQ(subscriptionPlanFormik)}
                              >
                                Add FAQ
                              </Button>
                            </div>
                            {subscriptionPlanFormik.values.faqs?.map((faq, index) => (
                              <Card key={index} className="mb-3">
                                <Card.Body>
                                  <div className="d-flex justify-content-between align-items-center mb-2 faq-header-styled">
                                    <h6 className="faq-title">FAQ </h6>
                                    {subscriptionPlanFormik.values.faqs.length > 1 && (
                                      <Button
                                        type="button"
                                        className="btn-filled"
                                        size="sm"
                                        onClick={() => handleRemoveFAQ(subscriptionPlanFormik, index)}
                                      >
                                        Remove
                                      </Button>
                                    )}
                                  </div>
                                  <Form.Group className="mb-3">
                                    <Form.Label>Question <span className="text-danger">*</span></Form.Label>
                                    <Form.Control
                                      type="text"
                                      value={faq.question || ""}
                                      onChange={(e) =>
                                        subscriptionPlanFormik.setFieldValue(
                                          `faqs.${index}.question`,
                                          e.target.value
                                        )
                                      }
                                      onBlur={() => subscriptionPlanFormik.setFieldTouched(`faqs.${index}.question`)}
                                      placeholder="Enter question"
                                    />
                                    {subscriptionPlanFormik.touched.faqs?.[index]?.question && subscriptionPlanFormik.errors.faqs?.[index]?.question && (
                                      <span className="text-danger d-block mt-1">{subscriptionPlanFormik.errors.faqs[index].question}</span>
                                    )}
                                  </Form.Group>
                                  <Form.Group className="mb-3">
                                    <Form.Label>Answer <span className="text-danger">*</span></Form.Label>
                                    <CKEditor
                                      editor={ClassicEditor}
                                      data={faq.answer || ""}
                                      onReady={(editor) => {
                                        if (!editor?.getData) return;
                                        const data = editor.getData();
                                        if ((subscriptionPlanFormik.values.faqs?.[index]?.answer || "") !== data) {
                                          subscriptionPlanFormik.setFieldValue(`faqs.${index}.answer`, data);
                                        }
                                      }}
                                      onChange={(event, editor) => {
                                        const data = editor.getData();
                                        subscriptionPlanFormik.setFieldValue(
                                          `faqs.${index}.answer`,
                                          data
                                        );
                                        subscriptionPlanFormik.setFieldTouched(`faqs.${index}.answer`, true);
                                      }}
                                    />
                                    {(() => {
                                      const answerError = subscriptionPlanFormik.errors.faqs?.[index]?.answer;
                                      const showError =
                                        answerError &&
                                        (subscriptionPlanFormik.touched.faqs?.[index]?.answer ||
                                          subscriptionPlanFormik.submitCount > 0);
                                      if (!showError) return null;
                                      const val = subscriptionPlanFormik.values.faqs?.[index]?.answer || "";
                                      const stripped = val
                                        .replace(/<[^>]*>/g, "")
                                        .replace(/&nbsp;/g, " ")
                                        .trim();
                                      if (stripped.length > 0) return null;
                                      return (
                                        <span className="text-danger d-block mt-1">{answerError}</span>
                                      );
                                    })()}
                                  </Form.Group>
                                </Card.Body>
                              </Card>
                            ))}
                          </Col>
                          <Col md={12}>
                            <Form.Group className="mb-3">
                              <Form.Label>
                                Price <span className="text-danger">*</span>
                              </Form.Label>
                              <Form.Control
                                type="text"
                                name="price"
                                value={subscriptionPlanFormik.values.price}
                                onChange={subscriptionPlanFormik.handleChange}
                                placeholder="Enter price (e.g., $39.99/month)"
                              />
                              {subscriptionPlanFormik.touched.price && subscriptionPlanFormik.errors.price && (
                                <span className="text-danger">{subscriptionPlanFormik.errors.price}</span>
                              )}
                            </Form.Group>
                          </Col>
                          <Col md={12}>
                            <Form.Group className="ml-3">
                              <Row>
                                <Col md={1}>
                                  <label className="label-font">Status</label>
                                </Col>
                                <Col md={11} className="check-inline d-flex">
                                  <label className="right-label-radio mr-3 mb-2">
                                    Active
                                    <input
                                      name="status"
                                      type="radio"
                                      checked={subscriptionPlanFormik.values.status === true}
                                      onChange={() => subscriptionPlanFormik.setFieldValue("status", true)}
                                    />
                                    <span className="checkmark"></span>
                                  </label>
                                  <label className="right-label-radio mr-3 mb-2">
                                    Inactive
                                    <input
                                      name="status"
                                      type="radio"
                                      checked={subscriptionPlanFormik.values.status === false}
                                      onChange={() => subscriptionPlanFormik.setFieldValue("status", false)}
                                    />
                                    <span className="checkmark"></span>
                                  </label>
                                </Col>
                              </Row>
                            </Form.Group>
                          </Col>
                        </Row>
                        <div className="d-flex justify-content-end mt-4">
                          <Button type="submit" className="btn-filled" disabled={loader}>
                            {loader ? "Saving..." : "Save Subscription Plan"}
                          </Button>
                        </div>
                      </Form>
                    </Tab>

                    {/* Subscription Guide Tab */}
                    <Tab eventKey="subscriptionGuide" title="Subscription Guide">
                      <Form onSubmit={subscriptionGuideFormik.handleSubmit}>
                        <Row>
                          <Col md={12}>
                            <Form.Group className="mb-3">
                              <Form.Label>
                                Section Heading <span className="text-danger">*</span>
                              </Form.Label>
                              <Form.Control
                                type="text"
                                name="heading"
                                value={subscriptionGuideFormik.values.heading}
                                onChange={subscriptionGuideFormik.handleChange}
                                placeholder="Enter section heading"
                              />
                            </Form.Group>
                          </Col>
                          <Col md={12}>
                            <Form.Group className="ml-3">
                              <Row>
                                <Col md={1}>
                                  <label className="label-font">Status</label>
                                </Col>
                                <Col md={11} className="check-inline d-flex">
                                  <label className="right-label-radio mr-3 mb-2">
                                    Active
                                    <input
                                      name="status"
                                      type="radio"
                                      checked={subscriptionGuideFormik.values.status === true}
                                      onChange={() => subscriptionGuideFormik.setFieldValue("status", true)}
                                    />
                                    <span className="checkmark"></span>
                                  </label>
                                  <label className="right-label-radio mr-3 mb-2">
                                    Inactive
                                    <input
                                      name="status"
                                      type="radio"
                                      checked={subscriptionGuideFormik.values.status === false}
                                      onChange={() => subscriptionGuideFormik.setFieldValue("status", false)}
                                    />
                                    <span className="checkmark"></span>
                                  </label>
                                </Col>
                              </Row>
                            </Form.Group>
                          </Col>
                        </Row>
                        <div className="d-flex justify-content-end mt-4">
                          <Button type="submit" className="btn-filled" disabled={loader}>
                            {loader ? "Saving..." : "Save Subscription Guide"}
                          </Button>
                        </div>
                      </Form>
                    </Tab>

                    {/* Better Choice Tab */}
                    <Tab eventKey="betterChoice" title="Better Choice">
                      <Form onSubmit={betterChoiceFormik.handleSubmit}>
                        <Row>
                          <Col md={12}>
                            <Form.Group className="mb-3">
                              <Form.Label>
                                Section Heading <span className="text-danger">*</span>
                              </Form.Label>
                              <Form.Control
                                type="text"
                                name="heading"
                                value={betterChoiceFormik.values.heading}
                                onChange={betterChoiceFormik.handleChange}
                                onBlur={betterChoiceFormik.handleBlur}
                                placeholder="Enter section heading"
                              />
                              {(betterChoiceFormik.touched.heading || betterChoiceFormik.submitCount > 0) && betterChoiceFormik.errors.heading && (
                                <span className="text-danger d-block mt-1">{betterChoiceFormik.errors.heading}</span>
                              )}
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Card className="mb-3">
                              <Card.Header>
                                <h4 className="mt-1">CareMD Details</h4>
                              </Card.Header>
                              <Card.Body>
                                <Form.Group className="mb-3">
                                  <Form.Label>Consultation Cost <span className="text-danger">*</span></Form.Label>
                                  <Form.Control
                                    type="text"
                                    value={betterChoiceFormik.values.caremd.consultationCost || ""}
                                    onChange={(e) =>
                                      betterChoiceFormik.setFieldValue("caremd.consultationCost", e.target.value)
                                    }
                                    onBlur={() => betterChoiceFormik.setFieldTouched("caremd.consultationCost")}
                                    placeholder="e.g., $39.99"
                                  />
                                  {betterChoiceFormik.touched.caremd?.consultationCost && betterChoiceFormik.errors.caremd?.consultationCost && (
                                    <span className="text-danger d-block mt-1">{betterChoiceFormik.errors.caremd.consultationCost}</span>
                                  )}
                                </Form.Group>
                                <Form.Group className="mb-3">
                                  <Form.Label>Patient Reviews <span className="text-danger">*</span></Form.Label>
                                  <Form.Control
                                    type="text"
                                    value={betterChoiceFormik.values.caremd.patientReviews || ""}
                                    onChange={(e) =>
                                      betterChoiceFormik.setFieldValue("caremd.patientReviews", e.target.value)
                                    }
                                    onBlur={() => betterChoiceFormik.setFieldTouched("caremd.patientReviews")}
                                    placeholder="e.g., 4.9"
                                  />
                                  {((betterChoiceFormik.touched.caremd?.patientReviews || betterChoiceFormik.submitCount > 0) && betterChoiceFormik.errors.caremd?.patientReviews) && (
                                    <span className="text-danger d-block mt-1">{betterChoiceFormik.errors.caremd.patientReviews}</span>
                                  )}
                                </Form.Group>
                                <Form.Group className="mb-3">
                                  <Form.Label>Consultation Method <span className="text-danger">*</span></Form.Label>
                                  <Form.Control
                                    type="text"
                                    value={betterChoiceFormik.values.caremd.consultationMethod || ""}
                                    onChange={(e) =>
                                      betterChoiceFormik.setFieldValue("caremd.consultationMethod", e.target.value)
                                    }
                                    onBlur={() => betterChoiceFormik.setFieldTouched("caremd.consultationMethod")}
                                    placeholder="e.g., Text & Phone Options"
                                  />
                                  {betterChoiceFormik.touched.caremd?.consultationMethod && betterChoiceFormik.errors.caremd?.consultationMethod && (
                                    <span className="text-danger d-block mt-1">{betterChoiceFormik.errors.caremd.consultationMethod}</span>
                                  )}
                                </Form.Group>
                                <Form.Group className="mb-3">
                                  <Form.Label>Requires Appointment? <span className="text-danger">*</span></Form.Label>
                                  <Form.Control
                                    type="text"
                                    value={betterChoiceFormik.values.caremd.requiresAppointment || ""}
                                    onChange={(e) =>
                                      betterChoiceFormik.setFieldValue("caremd.requiresAppointment", e.target.value)
                                    }
                                    onBlur={() => betterChoiceFormik.setFieldTouched("caremd.requiresAppointment")}
                                    placeholder="e.g., Not required"
                                  />
                                  {((betterChoiceFormik.touched.caremd?.requiresAppointment || betterChoiceFormik.submitCount > 0) && betterChoiceFormik.errors.caremd?.requiresAppointment) && (
                                    <span className="text-danger d-block mt-1">{betterChoiceFormik.errors.caremd.requiresAppointment}</span>
                                  )}
                                </Form.Group>
                                <Form.Group className="mb-3 availability-check">
                                  <Form.Label>24/7/365?</Form.Label>
                                  <Form.Check
                                    type="checkbox"
                                    checked={betterChoiceFormik.values.caremd.is247 || false}
                                    className="pl-0"
                                    onChange={(e) =>
                                      betterChoiceFormik.setFieldValue("caremd.is247", e.target.checked)
                                    }
                                    label="Available 24/7/365"
                                  />
                                </Form.Group>
                              </Card.Body>
                            </Card>
                          </Col>
                          <Col md={6}>
                            <Card className="mb-3">
                              <Card.Header>
                                <h4 className="mt-1">Other Telemedicine Details</h4>
                              </Card.Header>
                              <Card.Body>
                                <Form.Group className="mb-3">
                                  <Form.Label>Consultation Cost <span className="text-danger">*</span></Form.Label>
                                  <Form.Control
                                    type="text"
                                    value={betterChoiceFormik.values.otherTelemedicine.consultationCost || ""}
                                    onChange={(e) =>
                                      betterChoiceFormik.setFieldValue("otherTelemedicine.consultationCost", e.target.value)
                                    }
                                    onBlur={() => betterChoiceFormik.setFieldTouched("otherTelemedicine.consultationCost")}
                                    placeholder="e.g., $80 - $250"
                                  />
                                  {((betterChoiceFormik.touched.otherTelemedicine?.consultationCost || betterChoiceFormik.submitCount > 0) && betterChoiceFormik.errors.otherTelemedicine?.consultationCost) && (
                                    <span className="text-danger d-block mt-1">{betterChoiceFormik.errors.otherTelemedicine.consultationCost}</span>
                                  )}
                                </Form.Group>
                                <Form.Group className="mb-3">
                                  <Form.Label>Patient Reviews <span className="text-danger">*</span></Form.Label>
                                  <Form.Control
                                    type="text"
                                    value={betterChoiceFormik.values.otherTelemedicine.patientReviews || ""}
                                    onChange={(e) =>
                                      betterChoiceFormik.setFieldValue("otherTelemedicine.patientReviews", e.target.value)
                                    }
                                    onBlur={() => betterChoiceFormik.setFieldTouched("otherTelemedicine.patientReviews")}
                                    placeholder="e.g., 3.5"
                                  />
                                  {((betterChoiceFormik.touched.otherTelemedicine?.patientReviews || betterChoiceFormik.submitCount > 0) && betterChoiceFormik.errors.otherTelemedicine?.patientReviews) && (
                                    <span className="text-danger d-block mt-1">{betterChoiceFormik.errors.otherTelemedicine.patientReviews}</span>
                                  )}
                                </Form.Group>
                                <Form.Group className="mb-3">
                                  <Form.Label>Consultation Method <span className="text-danger">*</span></Form.Label>
                                  <Form.Control
                                    type="text"
                                    value={betterChoiceFormik.values.otherTelemedicine.consultationMethod || ""}
                                    onChange={(e) =>
                                      betterChoiceFormik.setFieldValue("otherTelemedicine.consultationMethod", e.target.value)
                                    }
                                    onBlur={() => betterChoiceFormik.setFieldTouched("otherTelemedicine.consultationMethod")}
                                    placeholder="e.g., *Most limited to video visits"
                                  />
                                  {((betterChoiceFormik.touched.otherTelemedicine?.consultationMethod || betterChoiceFormik.submitCount > 0) && betterChoiceFormik.errors.otherTelemedicine?.consultationMethod) && (
                                    <span className="text-danger d-block mt-1">{betterChoiceFormik.errors.otherTelemedicine.consultationMethod}</span>
                                  )}
                                </Form.Group>
                                <Form.Group className="mb-3">
                                  <Form.Label>Requires Appointment? <span className="text-danger">*</span></Form.Label>
                                  <Form.Control
                                    type="text"
                                    value={betterChoiceFormik.values.otherTelemedicine.requiresAppointment || ""}
                                    onChange={(e) =>
                                      betterChoiceFormik.setFieldValue("otherTelemedicine.requiresAppointment", e.target.value)
                                    }
                                    onBlur={() => betterChoiceFormik.setFieldTouched("otherTelemedicine.requiresAppointment")}
                                    placeholder="e.g., Required"
                                  />
                                  {((betterChoiceFormik.touched.otherTelemedicine?.requiresAppointment || betterChoiceFormik.submitCount > 0) && betterChoiceFormik.errors.otherTelemedicine?.requiresAppointment) && (
                                    <span className="text-danger d-block mt-1">{betterChoiceFormik.errors.otherTelemedicine.requiresAppointment}</span>
                                  )}
                                </Form.Group>
                                <Form.Group className="mb-3 availability-check">
                                  <Form.Label>24/7/365?</Form.Label>
                                  <Form.Check
                                    type="checkbox"
                                      className="pl-0"
                                    checked={betterChoiceFormik.values.otherTelemedicine.is247 || false}
                                    onChange={(e) =>
                                      betterChoiceFormik.setFieldValue("otherTelemedicine.is247", e.target.checked)
                                    }
                                    label="Available 24/7/365"
                                  />
                                </Form.Group>
                              </Card.Body>
                            </Card>
                          </Col>
                          <Col md={12}>
                            <Form.Group className="ml-3">
                              <Row>
                                <Col md={1}>
                                  <label className="label-font">Status</label>
                                </Col>
                                <Col md={11} className="check-inline d-flex">
                                  <label className="right-label-radio mr-3 mb-2">
                                    Active
                                    <input
                                      name="status"
                                      type="radio"
                                      checked={betterChoiceFormik.values.status === true}
                                      onChange={() => betterChoiceFormik.setFieldValue("status", true)}
                                    />
                                    <span className="checkmark"></span>
                                  </label>
                                  <label className="right-label-radio mr-3 mb-2">
                                    Inactive
                                    <input
                                      name="status"
                                      type="radio"
                                      checked={betterChoiceFormik.values.status === false}
                                      onChange={() => betterChoiceFormik.setFieldValue("status", false)}
                                    />
                                    <span className="checkmark"></span>
                                  </label>
                                </Col>
                              </Row>
                            </Form.Group>
                          </Col>
                        </Row>
                        <div className="d-flex justify-content-end mt-4">
                          <Button type="submit" className="btn-filled" disabled={loader}>
                            {loader ? "Saving..." : "Save Better Choice"}
                          </Button>
                        </div>
                      </Form>
                    </Tab>
                  </Tabs>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </>
  );
};

export default SubscribePageManagement;
